import { IksaShoppingBagTypeEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { ICart, IShoppingItem, IShoppingList } from '@inter-ikea-kompis/types';

import ServiceFetch from '../../../utilities/ServiceFetch.js';
import type IIksaCreateShoppingBagRequestBody from './types/IIksaCreateShoppingBagRequestBody.js';
import type IIksaShoppingBagCartInfo from './types/IIksaShoppingBagCartInfo.js';
import type IIksaShoppingBagErrorResponse from './types/IIksaShoppingBagErrorResponse.js';
import type IIksaShoppingBagResponse from './types/IIksaShoppingBagResponse.js';
import type IIksaShoppingBagsResponse from './types/IIksaShoppingBagsResponse.js';
import type IIksaShoppingBagSuccessResponse from './types/IIksaShoppingBagSuccessResponse.js';
import type IUpdateShoppingBagItemsRequestBody from './types/IUpdateShoppingBagItemsRequestBody.js';
import IksaShoppingBagErrorCodes from './IksaShoppingBagErrorCodes.js';

/**
 * This class handles CRUD operations for IKSA shopping cart API.
 */
export default class IksaShoppingBagService {
	private readonly consumer: string;
	private readonly contract: string;
	private readonly endpoint: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.consumer Consumer.
	 * @param options.contract Contract.
	 * @param options.endpoint Endpoint.
	 */
	constructor(options: { consumer: string; contract: string; endpoint: string }) {
		this.consumer = options.consumer;
		this.contract = options.contract;
		this.endpoint = options.endpoint;
	}

	/**
	 * Adds items to shopping cart.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.items Array of shopping items.
	 * @returns Promise object.
	 */
	public async addToCart(options: { userId: string; items: IShoppingItem[] }): Promise<ICart> {
		return await this.addToBag({
			userId: options.userId,
			items: options.items,
			shoppingBagType: IksaShoppingBagTypeEnum.cart
		});
	}

	/**
	 * Adds items to shopping list.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.items Array of shopping items.
	 * @param options.shoppingListName List name.
	 * @returns Promise object.
	 */
	public async addToList(options: {
		userId: string;
		items: IShoppingItem[];
		shoppingListName: string;
	}): Promise<IShoppingList> {
		const cart = await this.addToBag({
			userId: options.userId,
			items: options.items,
			shoppingBagType: IksaShoppingBagTypeEnum.list,
			shoppingListName: options.shoppingListName
		});
		return { listCount: cart.cartCount, errorList: cart.errorList };
	}

	/**
	 * Returns cart count for a shopping cart.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @returns Promise object.
	 */
	public async getCartCount(options: { userId: string }): Promise<ICart['cartCount']> {
		const cartInfo = await this.getCartInfo({
			userId: options.userId,
			shoppingBagType: IksaShoppingBagTypeEnum.cart
		});

		return cartInfo
			? (
					await this.getCart({
						userId: options.userId,
						shoppingBagType: IksaShoppingBagTypeEnum.cart,
						cartId: cartInfo.id
					})
				).cartCount
			: 0;
	}

	/**
	 * Returns list count from a shopping list.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.shoppingListName List name.
	 * @returns Promise object.
	 */
	public async getListCount(options: {
		userId: string;
		shoppingListName: string;
	}): Promise<IShoppingList['listCount']> {
		const cartInfo = await this.getCartInfo({
			userId: options.userId,
			shoppingBagType: IksaShoppingBagTypeEnum.list,
			shoppingListName: options.shoppingListName
		});

		return cartInfo
			? (
					await this.getCart({
						userId: options.userId,
						shoppingBagType: IksaShoppingBagTypeEnum.list,
						cartId: cartInfo.id
					})
				).cartCount
			: 0;
	}

	/**
	 * Adds items to shopping bag.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.items Array of shopping items.
	 * @param options.shoppingBagType Cart type.
	 * @param options.shoppingListName List name.
	 * @returns Promise of ICart.
	 */
	private async addToBag(options: {
		userId: string;
		items: IShoppingItem[];
		shoppingBagType: IksaShoppingBagTypeEnum;
		shoppingListName?: string;
	}): Promise<ICart> {
		const cartInfo = await this.getCartInfo({
			userId: options.userId,
			shoppingBagType: options.shoppingBagType
		});

		if (cartInfo !== null) {
			return await this.updateShoppingBagItems({ ...options, cartId: cartInfo.id });
		}

		return await this.createBag({
			items: options.items,
			userId: options.userId,
			shoppingBagType: options.shoppingBagType,
			shoppingListName: options.shoppingListName
		});
	}

	/**
	 * Returns cart information (name and id) if a cart exists.
	 * Returns null if the cart doesn't exist.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.shoppingBagType Cart type.
	 * @param options.shoppingListName List name.
	 * @returns Promise of IIksaShoppingBagCartInfo or null.
	 */
	private async getCartInfo(options: {
		userId: string;
		shoppingBagType: IksaShoppingBagTypeEnum;
		shoppingListName?: string;
	}): Promise<IIksaShoppingBagCartInfo | null> {
		const response = await ServiceFetch.json(
			this.getEndpointUrl(options.userId, options.shoppingBagType),
			{
				headers: this.getHeaders(),
				credentials: 'include'
			}
		);

		const iksaResponse = <IIksaShoppingBagsResponse>response;
		if (iksaResponse.length === 0) {
			return null;
		}

		const cart = iksaResponse[0];
		return {
			id: cart.bagId,
			type: cart.bagType,
			name: options.shoppingListName ?? null
		};
	}

	/**
	 * Returns a shopping cart.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.cartId Cart id.
	 * @param options.shoppingBagType Cart type.
	 * @returns Promise object.
	 */
	private async getCart(options: {
		userId: string;
		cartId: string;
		shoppingBagType: IksaShoppingBagTypeEnum;
	}): Promise<ICart> {
		const endpointUrl = this.getEndpointUrl(options.userId, options.shoppingBagType);
		const url = `${endpointUrl}/${options.cartId}`;
		const response = await ServiceFetch.json(url, {
			headers: this.getHeaders(),
			credentials: 'include'
		});

		return this.parseShoppingBagResponse(<IIksaShoppingBagResponse>response);
	}

	/**
	 * @param response The response to parse.
	 * @returns ICart parsed from response.
	 */
	private parseShoppingBagResponse(response: IIksaShoppingBagResponse): ICart {
		if ('errorList' in response) {
			return this.parseShoppingBagErrorResponse(response);
		}
		return this.parseShoppingBagSuccessResponse(response);
	}

	/**
	 * @param response The error response to parse.
	 * @returns ICart parsed from error response.
	 */
	private parseShoppingBagErrorResponse(response: IIksaShoppingBagErrorResponse): ICart {
		const errorList: { code: string; message: string; items: string[] }[] = [];

		for (const error of response.errorList) {
			const items: string[] = [];
			for (const attributes of error.errorAttributeList ?? []) {
				items.push(attributes.value);
			}
			const code = error.errorCode ? IksaShoppingBagErrorCodes[error.errorCode] : null;
			errorList.push({
				message: error.errorMessage ?? '',
				code: code ?? '',
				items
			});
		}

		return {
			cartCount: null,
			errorList
		};
	}

	/**
	 * @param response The success response to parse.
	 * @returns ICart parsed from success response.
	 */
	private parseShoppingBagSuccessResponse(response: IIksaShoppingBagSuccessResponse): ICart {
		const itemList = response.shoppingBagSectionList?.[0];

		if (!itemList || !itemList.shoppingBagItemList) {
			throw new ServiceException('Unexpected shopping bag response!');
		}

		let cartCount = 0;

		const items = itemList.shoppingBagItemList;
		for (const item of items) {
			cartCount += Number(item.itemQty);
		}

		return { cartCount, errorList: [] };
	}

	/**
	 * Updates shopping bag items.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.shoppingBagType Cart type.
	 * @param options.cartId Cart id.
	 * @param options.items Items to use.
	 * @returns Promise object.
	 */
	private async updateShoppingBagItems(options: {
		userId: string;
		shoppingBagType: IksaShoppingBagTypeEnum;
		cartId: string;
		items: IShoppingItem[];
	}): Promise<ICart> {
		const endpointUrl = this.getEndpointUrl(options.userId, options.shoppingBagType);
		const url = `${endpointUrl}/${options.cartId}/items`;

		const response = await ServiceFetch.text(url, {
			method: 'PUT',
			headers: this.getHeaders(),
			credentials: 'include',
			body: JSON.stringify(this.createUpdateShoppingBagItemsRequestBody(options.items))
		});

		if (typeof response === 'string' && response.startsWith('{"errorList"')) {
			return this.parseShoppingBagErrorResponse(
				<IIksaShoppingBagErrorResponse>JSON.parse(response)
			);
		}

		return await this.getCart(options);
	}

	/**
	 * @param items The shopping items to add to shopping bag.
	 * @returns Request body including the shopping items.
	 */
	private createUpdateShoppingBagItemsRequestBody(
		items: IShoppingItem[]
	): IUpdateShoppingBagItemsRequestBody {
		const itemList: IUpdateShoppingBagItemsRequestBody = [];

		for (const item of items) {
			this.validateItem(item);
			itemList.push({
				itemType: item.type,
				itemNo: item.id,
				itemQty: item.quantity
			});
		}

		return itemList;
	}

	/**
	 * Creates a shopping bag.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.shoppingBagType Cart type.
	 * @param options.items Items to use.
	 * @param options.shoppingListName List name.
	 * @returns Promise of ICart.
	 */
	private async createBag(options: {
		userId: string;
		items: IShoppingItem[];
		shoppingBagType: IksaShoppingBagTypeEnum;
		shoppingListName?: string;
	}): Promise<ICart> {
		const url = this.getEndpointUrl(options.userId, options.shoppingBagType);
		const modelOptions = {
			userId: options.userId,
			items: options.items,
			shoppingBagType: options.shoppingBagType,
			shoppingListName: options.shoppingListName
		};

		const response = await ServiceFetch.json(url, {
			method: 'POST',
			headers: this.getHeaders(),
			credentials: 'include',
			body: JSON.stringify(this.createShoppingCartRequestBody(modelOptions))
		});

		return this.parseShoppingBagResponse(<IIksaShoppingBagResponse>response);
	}

	/**
	 * Generates a shopping cart request body.
	 *
	 * @param options Options.
	 * @param options.userId User ID.
	 * @param options.shoppingBagType Cart type.
	 * @param options.items Items to use.
	 * @param options.shoppingListName List name.
	 * @returns Shopping cart request body.
	 */
	private createShoppingCartRequestBody(options: {
		userId: string;
		shoppingBagType: IksaShoppingBagTypeEnum;
		items: IShoppingItem[];
		shoppingListName?: string;
	}): IIksaCreateShoppingBagRequestBody {
		const shoppingListName = options.shoppingListName || options.shoppingBagType;
		return {
			bagType: options.shoppingBagType.toUpperCase(),
			bagSource: 'IRW',
			bagName: shoppingListName,
			customerSource: 'IRW',
			customerId: options.userId,
			createdDateTime: this.getCreatedTime(),
			shoppingBagSectionList: [
				{
					shoppingBagItemList: options.items.map((item) => {
						this.validateItem(item);
						return {
							itemType: item.type.toUpperCase(),
							itemNo: item.id,
							itemQty: item.quantity.toString()
						};
					})
				}
			]
		};
	}

	/**
	 * @param item A shopping item.
	 * @throws ServiceException if item is invalid.
	 */
	private validateItem(item: IShoppingItem): void {
		if (
			typeof item.id !== 'string' ||
			typeof item.type !== 'string' ||
			typeof item.quantity !== 'number'
		) {
			throw new ServiceException(
				'Item was not in a valid format. Please use this format: { id: "123456", type: "ART", quantity: 1 }'
			);
		}
	}

	/**
	 * Returns headers to be used by requests.
	 *
	 * @returns Object as key value pairs of headers.
	 */
	private getHeaders(): HeadersInit {
		return {
			accept: 'application/vnd.middleware-v1+json',
			contract: this.contract,
			consumer: this.consumer,
			'content-type': 'application/json'
		};
	}

	/**
	 * Returns endpoint URL.
	 *
	 * @param userId User id.
	 * @param shoppingBagType Bag type.
	 * @returns Endpoint url.
	 */
	private getEndpointUrl(userId: string, shoppingBagType: IksaShoppingBagTypeEnum): string {
		return this.endpoint.replace('{userId}', userId).replace('{bagType}', shoppingBagType);
	}

	/**
	 * Returns a date ISO string of the current time.
	 *
	 * @returns Date ISO string.
	 */
	private getCreatedTime(): string {
		return new Date().toISOString();
	}
}
